import React, { useState, useEffect } from 'react';
import { COUNTRIES, searchCities, normalizeCity } from '../data/countries';

function LocationSelector({ 
  country, 
  state, 
  city, 
  onCountryChange, 
  onStateChange, 
  onCityChange,
  required = false,
  prefix = '',
  fieldErrors = {} // { country: 'msg', state: 'msg', city: 'msg' }
}) {
  const [citySearch, setCitySearch] = useState(city || '');
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const [citySuggestions, setCitySuggestions] = useState([]);

  useEffect(() => {
    setCitySearch(city || '');
  }, [city]);

  useEffect(() => {
    if (country && state && citySearch) {
      const suggestions = searchCities(country, state, citySearch);
      setCitySuggestions(suggestions.slice(0, 10)); // Show top 10 matches
    } else if (country && state) {
      const allCities = searchCities(country, state, '');
      setCitySuggestions(allCities.slice(0, 10));
    } else {
      setCitySuggestions([]);
    }
  }, [country, state, citySearch]);

  const handleCountryChange = (e) => {
    const newCountry = e.target.value;
    onCountryChange(newCountry);
    // If the country has only one state (simple country), auto-select it
    if (newCountry && COUNTRIES[newCountry]) {
      const stateKeys = Object.keys(COUNTRIES[newCountry].states);
      if (stateKeys.length === 1) {
        onStateChange(stateKeys[0]);
      } else {
        onStateChange('');
      }
    } else {
      onStateChange('');
    }
    onCityChange(''); // Reset city
    setCitySearch('');
  };

  const handleStateChange = (e) => {
    const newState = e.target.value;
    onStateChange(newState);
    onCityChange(''); // Reset city
    setCitySearch('');
  };

  const handleCityInputChange = (e) => {
    const value = e.target.value;
    setCitySearch(value);
    setShowCitySuggestions(true);
    onCityChange(normalizeCity(value));
  };

  const handleCitySelect = (selectedCity) => {
    setCitySearch(selectedCity);
    onCityChange(selectedCity);
    setShowCitySuggestions(false);
  };

  const handleCityBlur = () => {
    // Delay to allow click on suggestion
    setTimeout(() => setShowCitySuggestions(false), 200);
  };

  const states = country && COUNTRIES[country] ? COUNTRIES[country].states : {};
  const stateKeys = Object.keys(states);
  const isSimpleCountry = stateKeys.length <= 1;

  // Sort countries alphabetically by name
  const sortedCountryCodes = Object.keys(COUNTRIES).sort((a, b) =>
    COUNTRIES[a].name.localeCompare(COUNTRIES[b].name)
  );

  return (
    <div>
      {/* Country Dropdown */}
      <div className="form-group">
        <label>{prefix ? `${prefix.charAt(0).toUpperCase() + prefix.slice(1)} ` : ''}Country *</label>
        <select
          value={country}
          onChange={handleCountryChange}
          required={required}
          style={fieldErrors.country ? { border: '2px solid #e74c3c' } : {}}
        >
          <option value="">Select country</option>
          {sortedCountryCodes.map(code => (
            <option key={code} value={code}>
              {COUNTRIES[code].name}
            </option>
          ))}
        </select>
        {fieldErrors.country && (
          <small style={{ color: '#e74c3c', fontSize: '12px', display: 'block', marginTop: '4px' }}>
            {fieldErrors.country}
          </small>
        )}
      </div>

      {/* State Dropdown - for detailed countries with state data */}
      {country && !isSimpleCountry && (
        <div className="form-group">
          <label>{prefix ? `${prefix.charAt(0).toUpperCase() + prefix.slice(1)} ` : ''}State/Region *</label>
          <select
            value={state}
            onChange={handleStateChange}
            required={required}
            style={fieldErrors.state ? { border: '2px solid #e74c3c' } : {}}
          >
            <option value="">Select state/region</option>
            {stateKeys.map(code => (
              <option key={code} value={code}>
                {states[code].name}
              </option>
            ))}
          </select>
          {fieldErrors.state && (
            <small style={{ color: '#e74c3c', fontSize: '12px', display: 'block', marginTop: '4px' }}>
              {fieldErrors.state}
            </small>
          )}
        </div>
      )}

      {/* State/Region text input - for simple countries without state data */}
      {country && isSimpleCountry && (
        <div className="form-group">
          <label>{prefix ? `${prefix.charAt(0).toUpperCase() + prefix.slice(1)} ` : ''}State/Region</label>
          <input
            type="text"
            value={state === 'OT' ? '' : state}
            onChange={(e) => onStateChange(e.target.value || 'OT')}
            placeholder="Enter state or region (optional)"
            autoComplete="off"
          />
        </div>
      )}

      {/* City Autocomplete - show when state is selected, or for simple countries (auto-selected) */}
      {country && (state || isSimpleCountry) && (
        <div className="form-group" style={{ position: 'relative' }}>
          <label>{prefix ? `${prefix.charAt(0).toUpperCase() + prefix.slice(1)} ` : ''}City *</label>
          <input
            type="text"
            value={citySearch}
            onChange={handleCityInputChange}
            onFocus={() => setShowCitySuggestions(true)}
            onBlur={handleCityBlur}
            placeholder={isSimpleCountry ? "Enter your city" : "Type to search..."}
            required={required}
            autoComplete="off"
            style={fieldErrors.city ? { border: '2px solid #e74c3c' } : {}}
          />
          
          {/* City Suggestions Dropdown */}
          {showCitySuggestions && citySuggestions.length > 0 && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              backgroundColor: 'white',
              border: '1px solid #ddd',
              borderRadius: '4px',
              maxHeight: '200px',
              overflowY: 'auto',
              zIndex: 1000,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              {citySuggestions.map((suggestedCity, index) => (
                <div
                  key={index}
                  onClick={() => handleCitySelect(suggestedCity)}
                  style={{
                    padding: '10px',
                    cursor: 'pointer',
                    borderBottom: index < citySuggestions.length - 1 ? '1px solid #eee' : 'none'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                >
                  {suggestedCity}
                </div>
              ))}
            </div>
          )}
          
          {showCitySuggestions && citySearch && citySuggestions.length === 0 && !isSimpleCountry && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              backgroundColor: 'white',
              border: '1px solid #ddd',
              borderRadius: '4px',
              padding: '10px',
              zIndex: 1000,
              color: '#666',
              fontSize: '14px'
            }}>
              No cities found. You can still enter a custom city name.
            </div>
          )}
          {fieldErrors.city && (
            <small style={{ color: '#e74c3c', fontSize: '12px', display: 'block', marginTop: '4px' }}>
              {fieldErrors.city}
            </small>
          )}
        </div>
      )}
    </div>
  );
}

export default LocationSelector;
